import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Settings, Users, Music2, Star, BarChart3, Megaphone, ShieldCheck, Send, ExternalLink, Globe2, Lock, Type, Save, RotateCcw, Sparkles, Plus, Trash2, Image as ImageIcon, Search, Filter, Pencil, Zap, ChefHat, Rocket, MessagesSquare, Building2, DollarSign, Heart, Flame, Trophy, Film } from "lucide-react";
import { AdminUserEditModal } from "../components/AdminUserEditModal";
import { AdminBatchImportPanel } from "../components/AdminBatchImportPanel";
import { AdminChallengesPanel } from "../components/AdminChallengesPanel";
import { AdminCreateArtistModal } from "../components/AdminCreateArtistModal";
import { AdminBulkSeedPanel } from "../components/AdminBulkSeedPanel";
import { AdminSurveysPanel } from "../components/AdminSurveysPanel";
import { AdminLeadsPanel } from "../components/AdminLeadsPanel";
import { AdminAdSensePanel } from "../components/AdminAdSensePanel";
import { AdminAdvertisersPanel } from "../components/AdminAdvertisersPanel";
import { AdminDatingPanel } from "../components/AdminDatingPanel";
import { AdminArnakPanel } from "../components/AdminArnakPanel";
import { AdminViralStudioPanel } from "../components/AdminViralStudioPanel";
import { AdminTopMadaPanel } from "../components/AdminTopMadaPanel";
import { AdminStudioIALocalPanel } from "../components/AdminStudioIALocalPanel";
import { AdminMadaWallsPanel } from "../components/AdminMadaWallsPanel";
import { AdminVideoStudioPanel } from "../components/AdminVideoStudioPanel";
import { AdminCategoriesPanel } from "../components/AdminCategoriesPanel";
import { AdminTopMadaVideosPanel } from "../components/AdminTopMadaVideosPanel";
import { CATEGORIES as CATS } from "../lib/categories";

const SECTION_LABELS = {
  home: "Accueil",
  explore: "Explorer",
  trending: "Top pays",
  rising: "Stars montantes",
  artists: "Artistes",
  featured: "En vedette",
  upload: "Upload",
  events: "Évènements",
  castings: "Appel à candidatures",
  library: "Bibliothèque",
  premium: "Premium",
  advertisers: "Annonceurs",
  vola: "Gagner des Vola",
  surveys: "Sondages",
  dating: "Rencontres ❤️",
};

const COUNTRY_FLAGS = {
  "Madagascar": "🇲🇬",
  "Nigeria": "🇳🇬",
  "Côte d'Ivoire": "🇨🇮",
  "Sénégal": "🇸🇳",
  "Kenya": "🇰🇪",
  "Cameroun": "🇨🇲",
  "RDC": "🇨🇩",
  "Congo": "🇨🇬",
  "Ghana": "🇬🇭",
  "Mali": "🇲🇱",
  "Afrique du Sud": "🇿🇦",
  "Algérie": "🇩🇿",
  "Maroc": "🇲🇦",
  "Tunisie": "🇹🇳",
  "Éthiopie": "🇪🇹",
  "Ouganda": "🇺🇬",
  "Rwanda": "🇷🇼",
  "Burkina Faso": "🇧🇫",
  "Bénin": "🇧🇯",
  "Togo": "🇹🇬",
  "Gabon": "🇬🇦",
  "Angola": "🇦🇴",
  "Zimbabwe": "🇿🇼",
  "Tanzanie": "🇹🇿",
  "France": "🇫🇷",
  "Réunion": "🇷🇪",
  "Maurice": "🇲🇺",
  "Comores": "🇰🇲",
};

const inputCls = "w-full rounded-xl bg-white/[0.03] border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-brand-orange/60 transition-colors";
const adInputCls = "w-full rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-orange/60";

const flagFor = (country) => COUNTRY_FLAGS[country] || "🌍";

export const AdminPage = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("sections");
  const isFullAdmin = !!user?.is_admin;
  const isArnakOnlyAdmin = !isFullAdmin && !!user?.is_arnak_admin;
  // Admin Voamada complet SANS accès à la rubrique Arnak (compte topmada par ex)
  const canAccessArnak = (isFullAdmin && !user?.no_arnak) || isArnakOnlyAdmin;
  // Studio Viral : whitelist d'emails (synchro avec admin_viral_studio.php côté backend)
  const VIRAL_WHITELIST = ["nousrenovons51@gmail.com", "topmada@gmail.com"];
  const canAccessViralStudio = VIRAL_WHITELIST.includes((user?.email || "").toLowerCase().trim());
  const [sections, setSections] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [ads, setAds] = useState([]);
  const [claims, setClaims] = useState([]);
  const [countries, setCountries] = useState([]);
  const [texts, setTexts] = useState({});
  const [textsDirty, setTextsDirty] = useState(false);

  // Users filters + edit modal
  const [userQ, setUserQ] = useState("");
  const [userFilter, setUserFilter] = useState({ category: "", country: "", role: "" });
  const [editingUser, setEditingUser] = useState(null);
  const [creatingArtist, setCreatingArtist] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    // Soit admin global Voamada, soit admin ARNAK restreint
    if (!user.is_admin && !user.is_arnak_admin) { navigate("/"); return; }
    // Si admin ARNAK uniquement → onglet "arnak" par défaut + skip refreshAll (qui hit /admin/* interdits)
    if (!user.is_admin && user.is_arnak_admin) {
      setTab("arnak");
      return;
    }
    refreshAll();
  }, [user, loading, navigate]);

  const refreshAll = () => {
    api.get("/admin/sections").then(({ data }) => setSections(data));
    api.get("/admin/stats").then(({ data }) => setStats(data));
    loadUsers();
    api.get("/admin/ads").then(({ data }) => setAds(data));
    api.get("/admin/claims").then(({ data }) => setClaims(data)).catch(() => {});
    api.get("/admin/countries").then(({ data }) => setCountries(data)).catch(() => {});
    api.get("/site-texts").then(({ data }) => { setTexts(data); setTextsDirty(false); }).catch(() => {});
  };

  const loadUsers = () => {
    const params = {};
    if (userQ) params.q = userQ;
    if (userFilter.category) params.category = userFilter.category;
    if (userFilter.country)  params.country = userFilter.country;
    if (userFilter.role)     params.role = userFilter.role;
    api.get("/admin/users", { params }).then(({ data }) => setUsers(data)).catch(() => {});
  };

  // Auto-refresh users on filters change (debounced)
  useEffect(() => {
    const t = setTimeout(() => { if (user && user.is_admin) loadUsers(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userQ, userFilter.category, userFilter.country, userFilter.role]);

  const saveTexts = async () => {
    try {
      await api.post("/admin/site-texts", { texts });
      setTextsDirty(false);
      toast.success("Textes enregistrés ✨ Recharge la page d'accueil pour voir les changements.");
    } catch (e) { toast.error("Erreur à l'enregistrement."); }
  };

  const resetText = (key, fallback) => {
    setTexts((t) => ({ ...t, [key]: fallback }));
    setTextsDirty(true);
  };

  const applyPreset = async (mode) => {
    const label = mode === "madagascar" ? "🇲🇬 Mode Madagascar" : "🌍 Mode Afrique";
    const msg = mode === "madagascar"
      ? "Ce preset va DÉSACTIVER tous les pays sauf Madagascar et basculer les textes sur une identité 100% gasy. Continuer ?"
      : "Ce preset va ACTIVER tous les pays et rétablir les textes \"Madagascar et d'Afrique\". Continuer ?";
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.post("/admin/preset/apply", { mode });
      toast.success(`${label} appliqué — ${data.countries_updated} pays, ${data.texts_updated} textes.`);
      refreshAll();
    } catch (e) { toast.error("Erreur lors de l'application du preset."); }
  };

  // ============ PUBLICITÉS CRUD ============
  const emptyAdForm = {
    title: "", description: "", placement: "banner",
    link_url: "", image_url: "",
    advertiser_name: "Voamada", advertiser_email: "",
    active: true,
    category: "", target_country: "",
    start_date: "", end_date: "",
    priority: 0, budget_ar: "",
  };
  const [adForm, setAdForm] = useState(emptyAdForm);
  const [editingAd, setEditingAd] = useState(null);
  const [adUploading, setAdUploading] = useState(false);
  const adFileRef = React.useRef(null);

  const fmtDtLocal = (s) => {
    if (!s) return "";
    try { return new Date(s.replace(" ", "T")).toISOString().slice(0, 16); } catch { return ""; }
  };

  const resetAdForm = () => {
    setAdForm(emptyAdForm);
    setEditingAd(null);
  };
  const startEditAd = (ad) => {
    setAdForm({
      title: ad.title || "", description: ad.description || "", placement: ad.placement || "banner",
      link_url: ad.link_url || "", image_url: ad.image_url || "",
      advertiser_name: ad.advertiser_name || "Voamada", advertiser_email: ad.advertiser_email || "",
      active: !!ad.active,
      category: ad.category || "",
      target_country: ad.target_country || "",
      start_date: fmtDtLocal(ad.start_date),
      end_date: fmtDtLocal(ad.end_date),
      priority: ad.priority || 0,
      budget_ar: ad.budget_ar ?? "",
    });
    setEditingAd(ad.ad_id);
    window.scrollTo({ top: 400, behavior: "smooth" });
  };
  const submitAd = async (e) => {
    e.preventDefault();
    if (!adForm.title || !adForm.placement) { toast.error("Titre et emplacement requis"); return; }
    try {
      if (editingAd) {
        const { data } = await api.patch(`/admin/ads/${editingAd}`, adForm);
        setAds((prev) => prev.map((a) => a.ad_id === editingAd ? data : a));
        toast.success("Publicité mise à jour");
      } else {
        const { data } = await api.post("/admin/ads", adForm);
        setAds((prev) => [data, ...prev]);
        toast.success("Publicité créée");
      }
      resetAdForm();
    } catch (err) { toast.error("Erreur"); }
  };
  const deleteAd = async (ad) => {
    if (!window.confirm(`Supprimer la pub "${ad.title}" ?`)) return;
    try {
      await api.delete(`/admin/ads/${ad.ad_id}`);
      setAds((prev) => prev.filter((a) => a.ad_id !== ad.ad_id));
      toast.success("Supprimée");
    } catch { toast.error("Erreur"); }
  };
  const uploadAdImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", "ad");
    setAdUploading(true);
    try {
      const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAdForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Image uploadée");
    } catch { toast.error("Upload échoué"); }
    finally { setAdUploading(false); }
  };

  const toggleCountry = async (country, enabled) => {
    try {
      await api.post("/admin/countries/toggle", { country, enabled });
      setCountries((prev) => prev.map((c) => (c.country === country ? { ...c, enabled } : c)));
      toast.success(`${country} ${enabled ? "activé" : "désactivé"}`);
    } catch (e) {
      toast.error("Impossible de modifier ce pays.");
    }
  };

  const bulkCountries = async (enabled) => {
    const targets = countries.filter((c) => !c.locked);
    if (!window.confirm(`${enabled ? "Activer" : "Désactiver"} ${targets.length} pays (hors Madagascar) ?`)) return;
    try {
      await Promise.all(targets.map((c) => api.post("/admin/countries/toggle", { country: c.country, enabled })));
      setCountries((prev) => prev.map((c) => (c.locked ? c : { ...c, enabled })));
      toast.success(`${targets.length} pays mis à jour.`);
    } catch (e) { toast.error("Erreur lors de la mise à jour."); }
  };

  const transferProfile = async (claim) => {
    const match = claim.link?.match(/\/artist\/([^/]+)/);
    const artistId = match?.[1];
    if (!artistId) { toast.error("ID artiste introuvable"); return; }
    const body = claim.body || "";
    const emailMatch = body.match(/Email\s*:\s*(\S+)/);
    const defaultEmail = emailMatch?.[1] || "";
    const newEmail = window.prompt(
      `Transférer le profil à quel email ?\n(email fourni par le demandeur : ${defaultEmail || "n/a"})`,
      defaultEmail
    );
    if (!newEmail) return;
    try {
      const { data } = await api.post(`/admin/claims/${claim.notif_id}/transfer`, {
        new_email: newEmail,
        artist_id: artistId,
      });
      toast.success(`Profil transféré ! Mot de passe temporaire : ${data.temp_password}`, { duration: 10000 });
      refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors du transfert");
    }
  };

  const toggleSection = async (section, enabled) => {
    await api.post("/admin/sections", { section, enabled });
    setSections(sections.map((s) => s.section === section ? { ...s, enabled } : s));
    toast.success(`${SECTION_LABELS[section] || section}: ${enabled ? "activé" : "désactivé"}`);
  };

  const updateUser = async (uid, field, value) => {
    await api.patch(`/admin/users/${uid}`, { [field]: value });
    setUsers(users.map((u) => u.user_id === uid ? { ...u, [field]: value } : u));
    toast.success("Utilisateur mis à jour");
  };

  const toggleAd = async (ad) => {
    await api.patch(`/admin/ads/${ad.ad_id}`, { active: !ad.active });
    setAds(ads.map((a) => a.ad_id === ad.ad_id ? { ...a, active: !a.active } : a));
  };

  if (loading) return <div className="px-6 py-20 text-center text-white/50">Chargement...</div>;
  if (!user || (!user.is_admin && !user.is_arnak_admin)) return null;

  return (
    <div className="px-6 md:px-12 py-10 max-w-7xl mx-auto pb-32" data-testid="admin-page">
      <p className="text-[10px] tracking-[0.25em] uppercase text-brand-red font-bold mb-2">
        {isArnakOnlyAdmin ? "Administration ARNAK" : "Administration"}
      </p>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mb-3">
        {isArnakOnlyAdmin ? "Panel ARNAK" : "Panel admin"}
      </h1>
      {isArnakOnlyAdmin && (
        <p className="text-sm text-white/55 mb-10" data-testid="arnak-admin-notice">
          Tu gères uniquement la section ARNAK (concerts, news, boutique, page RADO, partenaires, chat…). Aucun accès aux utilisateurs Voamada, pubs ou autres modules.
        </p>
      )}

      {/* QUICK PRESETS — réservé full admin */}
      {!isArnakOnlyAdmin && (
      <div className="rounded-2xl border border-brand-yellow/20 bg-gradient-to-br from-brand-red/5 via-brand-orange/5 to-brand-yellow/5 p-5 md:p-6 mb-10 mt-7" data-testid="admin-presets">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles size={16} className="text-brand-yellow" />
          <p className="font-display text-lg font-black">Presets rapides</p>
          <span className="text-xs text-white/40">Bascule toute la plateforme en un clic — textes + visibilité des pays.</span>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <button
            onClick={() => applyPreset("madagascar")}
            className="group text-left rounded-xl border border-white/10 hover:border-brand-red/50 bg-white/[0.02] hover:bg-brand-red/5 p-5 transition-all"
            data-testid="preset-madagascar-btn"
          >
            <p className="font-display text-xl font-black flex items-center gap-2 mb-1">🇲🇬 Mode Madagascar</p>
            <p className="text-xs text-white/55 leading-relaxed">
              Désactive tous les pays sauf Madagascar, bascule les textes sur une identité 100% malgache. Idéal pour le lancement local.
            </p>
            <span className="inline-block mt-3 text-xs font-semibold text-brand-red group-hover:translate-x-1 transition-transform">
              Appliquer →
            </span>
          </button>

          <button
            onClick={() => applyPreset("africa")}
            className="group text-left rounded-xl border border-white/10 hover:border-brand-yellow/50 bg-white/[0.02] hover:bg-brand-yellow/5 p-5 transition-all"
            data-testid="preset-africa-btn"
          >
            <p className="font-display text-xl font-black flex items-center gap-2 mb-1">🌍 Mode Afrique</p>
            <p className="text-xs text-white/55 leading-relaxed">
              Active tous les pays et rétablit les textes "Madagascar et d'Afrique". Ouvre la plateforme au continent entier.
            </p>
            <span className="inline-block mt-3 text-xs font-semibold text-brand-yellow group-hover:translate-x-1 transition-transform">
              Appliquer →
            </span>
          </button>
        </div>
      </div>
      )}

      {/* Stats bento — réservé full admin */}
      {!isArnakOnlyAdmin && stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <StatCard label="Utilisateurs" value={stats.users} />
          <StatCard label="Artistes" value={stats.artists} accent />
          <StatCard label="Morceaux" value={stats.tracks} />
          <StatCard label="Total écoutes" value={stats.plays} accent />
          <StatCard label="Audio" value={stats.audio} small />
          <StatCard label="Vidéo" value={stats.video} small />
          <StatCard label="Likes" value={stats.likes} small />
          <StatCard label="Commentaires" value={stats.comments} small />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-8 border-b border-white/10 overflow-x-auto -mx-6 px-6 md:mx-0 md:px-0 admin-tabs-scroll" data-testid="admin-tabs">
        {!isArnakOnlyAdmin && <>
        <TabBtn active={tab === "sections"} onClick={() => setTab("sections")} icon={<Settings size={14} />} label="Sections" testid="tab-sections" />
        <TabBtn active={tab === "countries"} onClick={() => setTab("countries")} icon={<Globe2 size={14} />} label="Pays" testid="tab-countries" />
        <TabBtn active={tab === "categories"} onClick={() => setTab("categories")} icon={<Filter size={14} />} label="Catégories" testid="tab-categories" />
        <TabBtn active={tab === "topmada"} onClick={() => setTab("topmada")} icon={<Trophy size={14} />} label="Top Mada" testid="tab-topmada" />
        <TabBtn active={tab === "texts"} onClick={() => setTab("texts")} icon={<Type size={14} />} label="Textes" testid="tab-texts" />
        <TabBtn active={tab === "users"} onClick={() => setTab("users")} icon={<Users size={14} />} label="Utilisateurs" testid="tab-users" />
        <TabBtn active={tab === "import"} onClick={() => setTab("import")} icon={<Zap size={14} />} label="Import vidéos" testid="tab-import" />
        <TabBtn active={tab === "seed"} onClick={() => setTab("seed")} icon={<Rocket size={14} />} label="Seed massif" testid="tab-seed" />
        <TabBtn active={tab === "challenges"} onClick={() => setTab("challenges")} icon={<ChefHat size={14} />} label="Défi cuisine" testid="tab-challenges" />
        <TabBtn active={tab === "ads"} onClick={() => setTab("ads")} icon={<Megaphone size={14} />} label="Publicités" testid="tab-ads" />
        <TabBtn active={tab === "surveys"} onClick={() => setTab("surveys")} icon={<MessagesSquare size={14} />} label="Sondages" testid="tab-surveys" />
        <TabBtn active={tab === "leads"} onClick={() => setTab("leads")} icon={<Building2 size={14} />} label="Leads annonceurs" testid="tab-leads" />
        <TabBtn active={tab === "advertisers"} onClick={() => setTab("advertisers")} icon={<Building2 size={14} />} label="Annonceurs" testid="tab-advertisers" />
        <TabBtn active={tab === "adsense"} onClick={() => setTab("adsense")} icon={<DollarSign size={14} />} label="AdSense" testid="tab-adsense" />
        <TabBtn active={tab === "dating"} onClick={() => setTab("dating")} icon={<Heart size={14} />} label="Rencontres" testid="tab-dating" />
        {canAccessViralStudio && <TabBtn active={tab === "viral"} onClick={() => setTab("viral")} icon={<Sparkles size={14} />} label="Studio Viral" testid="tab-viral" />}
        {canAccessViralStudio && <TabBtn active={tab === "madawalls"} onClick={() => setTab("madawalls")} icon={<MessagesSquare size={14} />} label="Murs Mada" testid="tab-madawalls" />}
        {canAccessViralStudio && <TabBtn active={tab === "videoai"} onClick={() => setTab("videoai")} icon={<Film size={14} />} label="Vidéo IA" testid="tab-videoai" />}
        <TabBtn active={tab === "studio-ia-local"} onClick={() => setTab("studio-ia-local")} icon={<Sparkles size={14} />} label="Studio IA Local" testid="tab-studio-ia-local" />
        </>}
        {canAccessArnak && <TabBtn active={tab === "arnak"} onClick={() => setTab("arnak")} icon={<Flame size={14} />} label="Arnak" testid="tab-arnak" />}
        {!isArnakOnlyAdmin && <TabBtn active={tab === "claims"} onClick={() => setTab("claims")} icon={<ShieldCheck size={14} />} label={`Réclamations${claims.filter((c) => !c.read).length > 0 ? ` (${claims.filter((c) => !c.read).length})` : ""}`} testid="tab-claims" />}
      </div>

      {tab === "sections" && (
        <div className="grid md:grid-cols-2 gap-4" data-testid="sections-panel">
          {sections.map((s) => (
            <div key={s.section} className="flex items-center justify-between p-4 rounded-xl border border-white/10 bg-white/[0.02]" data-testid={`section-${s.section}`}>
              <div>
                <p className="font-semibold">{SECTION_LABELS[s.section] || s.section}</p>
                <p className="text-xs text-white/50">Section /{s.section}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={s.enabled} onChange={(e) => toggleSection(s.section, e.target.checked)} className="sr-only peer" data-testid={`toggle-${s.section}`} />
                <div className="w-11 h-6 bg-white/10 rounded-full peer peer-checked:bg-brand-red transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" />
              </label>
            </div>
          ))}
        </div>
      )}

      {tab === "categories" && <AdminCategoriesPanel />}

      {tab === "countries" && (
        <div data-testid="countries-panel">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div>
              <p className="font-display text-xl font-black">Visibilité par pays</p>
              <p className="text-xs text-white/50">Désactive les pays que tu ne veux pas montrer côté public. Madagascar reste toujours actif.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => bulkCountries(false)} className="btn-ghost text-xs px-4 py-2" data-testid="bulk-disable-countries">Désactiver tout (sauf MG)</button>
              <button onClick={() => bulkCountries(true)} className="btn-primary text-xs px-4 py-2" data-testid="bulk-enable-countries">Tout activer</button>
            </div>
          </div>
          {countries.length === 0 ? (
            <p className="text-white/50 text-sm py-10 text-center">Aucun pays détecté.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {countries.map((c) => (
                <div
                  key={c.country}
                  className={`flex items-center justify-between p-4 rounded-xl border transition-colors ${
                    c.enabled
                      ? "border-brand-red/30 bg-brand-red/5"
                      : "border-white/10 bg-white/[0.02]"
                  }`}
                  data-testid={`country-row-${c.country}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-xl">{flagFor(c.country)}</span>
                    <div className="min-w-0">
                      <p className="font-semibold truncate flex items-center gap-1.5">
                        {c.country}
                        {c.locked && <Lock size={11} className="text-brand-yellow" />}
                      </p>
                      <p className="text-[11px] text-white/40">{c.enabled ? "Visible publiquement" : "Masqué du public"}</p>
                    </div>
                  </div>
                  <label className={`relative inline-flex items-center ${c.locked ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}>
                    <input
                      type="checkbox"
                      checked={c.enabled}
                      disabled={c.locked}
                      onChange={(e) => toggleCountry(c.country, e.target.checked)}
                      className="sr-only peer"
                      data-testid={`toggle-country-${c.country}`}
                    />
                    <div className="w-11 h-6 bg-white/10 rounded-full peer peer-checked:bg-brand-red transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" />
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "texts" && (
        <div data-testid="texts-panel">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div>
              <p className="font-display text-xl font-black">Textes de la page d'accueil</p>
              <p className="text-xs text-white/50">Personnalise le hero, les badges et les boutons d'action. Laisse vide pour revenir au texte par défaut.</p>
            </div>
            {textsDirty && (
              <button onClick={saveTexts} className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="save-texts-btn">
                <Save size={14} /> Enregistrer
              </button>
            )}
          </div>

          {/* Preview */}
          <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-brand-red/5 via-transparent to-brand-orange/5 p-6 mb-6" data-testid="texts-preview">
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-3">Aperçu</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {texts["hero.badge1"] && <span className="tag-pill border-brand-red/40 bg-brand-red/10 text-brand-red text-xs">🔥 {texts["hero.badge1"]}</span>}
              {texts["hero.badge2"] && <span className="tag-pill border-[#0B8B47]/40 bg-[#0B8B47]/10 text-[#12A859] text-xs">✓ {texts["hero.badge2"]}</span>}
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-black tracking-tighter mb-3 text-balance">
              {texts["hero.title_prefix"]}{" "}
              <span className="bg-gradient-to-r from-[#E31B23] via-brand-orange to-[#0B8B47] bg-clip-text text-transparent">
                {texts["hero.title_accent"]}
              </span>
              {texts["hero.title_suffix"] && <> {texts["hero.title_suffix"]}</>}
            </h2>
            <p className="text-white/60 text-sm max-w-2xl">{texts["hero.subtitle"]}</p>
            <div className="flex flex-wrap gap-2 mt-4">
              <span className="px-3 py-1.5 rounded-full bg-brand-red text-white text-xs font-bold">{texts["hero.cta_primary"]}</span>
              <span className="px-3 py-1.5 rounded-full border border-white/15 text-white text-xs font-bold">{texts["hero.cta_ghost"]}</span>
            </div>
          </div>

          <div className="grid gap-4">
            {[
              { key: "hero.title_prefix", label: "Titre — préfixe", hint: "Texte avant l'accent coloré", fallback: "Les talents de" },
              { key: "hero.title_accent", label: "Titre — mot clé coloré", hint: "Le mot mis en évidence avec dégradé (ex: Madagascar)", fallback: "Madagascar" },
              { key: "hero.title_suffix", label: "Titre — suffixe", hint: "Texte après le mot coloré. Laisse vide si tu ne veux plus \"et d'Afrique\".", fallback: "et d'Afrique.", allowEmpty: true },
              { key: "hero.subtitle",     label: "Sous-titre / description", hint: "Paragraphe descriptif sous le titre", fallback: "Voamada rassemble musiciens, humoristes, danseurs, cinéastes, artistes visuels, stylistes, créateurs. Un tremplin pour tous les talents du continent.", multiline: true },
              { key: "hero.badge1",       label: "Badge 1 (rouge)",  hint: "Petit pill en haut. Laisse vide pour masquer.", fallback: "Nouvelle plateforme pour artistes africains", allowEmpty: true },
              { key: "hero.badge2",       label: "Badge 2 (vert)",   hint: "Laisse vide pour masquer.", fallback: "100% gratuit", allowEmpty: true },
              { key: "hero.cta_primary",  label: "Bouton principal", hint: "Call-to-action principal (rouge)", fallback: "Déposer ma graine" },
              { key: "hero.cta_ghost",    label: "Bouton secondaire", hint: "Call-to-action transparent", fallback: "Explorer les artistes" },
            ].map((field) => (
              <div key={field.key} className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid={`text-field-${field.key}`}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-sm font-semibold">{field.label}</p>
                    <p className="text-[11px] text-white/40">{field.hint}</p>
                  </div>
                  <button
                    onClick={() => resetText(field.key, field.fallback)}
                    className="text-[11px] text-white/50 hover:text-white inline-flex items-center gap-1"
                    title="Remettre la valeur par défaut"
                    data-testid={`reset-${field.key}`}
                  >
                    <RotateCcw size={11} /> Défaut
                  </button>
                </div>
                {field.multiline ? (
                  <textarea
                    rows={3}
                    value={texts[field.key] ?? ""}
                    onChange={(e) => { setTexts((t) => ({ ...t, [field.key]: e.target.value })); setTextsDirty(true); }}
                    className="w-full rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-orange/60"
                  />
                ) : (
                  <input
                    type="text"
                    value={texts[field.key] ?? ""}
                    onChange={(e) => { setTexts((t) => ({ ...t, [field.key]: e.target.value })); setTextsDirty(true); }}
                    placeholder={field.allowEmpty ? "(vide = masqué)" : field.fallback}
                    className="w-full rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-orange/60"
                  />
                )}
              </div>
            ))}
          </div>

          {textsDirty && (
            <div className="mt-6 flex justify-end">
              <button onClick={saveTexts} className="btn-primary inline-flex items-center gap-2" data-testid="save-texts-btn-bottom">
                <Save size={16} /> Enregistrer les modifications
              </button>
            </div>
          )}
        </div>
      )}

      {tab === "users" && (
        <div className="space-y-4" data-testid="users-panel">
          {/* Search + filters bar */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 grid md:grid-cols-[1.2fr_1fr_1fr_1fr_auto] gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                value={userQ}
                onChange={(e) => setUserQ(e.target.value)}
                placeholder="Rechercher par nom, email, bio, ville…"
                className="w-full rounded-lg bg-white/[0.03] border border-white/10 pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-brand-orange/60"
                data-testid="admin-users-search"
              />
            </div>
            <select
              value={userFilter.category}
              onChange={(e) => setUserFilter({ ...userFilter, category: e.target.value })}
              className="rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-orange/60"
              data-testid="admin-users-filter-category"
            >
              <option value="">Toutes catégories</option>
              {CATS.map((c) => (
                <option key={c.key} value={c.key}>{c.emoji} {c.label}</option>
              ))}
            </select>
            <select
              value={userFilter.country}
              onChange={(e) => setUserFilter({ ...userFilter, country: e.target.value })}
              className="rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-orange/60"
              data-testid="admin-users-filter-country"
            >
              <option value="">Tous pays</option>
              {Object.keys(COUNTRY_FLAGS).map((c) => (
                <option key={c} value={c}>{COUNTRY_FLAGS[c]} {c}</option>
              ))}
            </select>
            <select
              value={userFilter.role}
              onChange={(e) => setUserFilter({ ...userFilter, role: e.target.value })}
              className="rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-orange/60"
              data-testid="admin-users-filter-role"
            >
              <option value="">Tous rôles</option>
              <option value="artist">Artistes</option>
              <option value="verified">Vérifiés</option>
              <option value="premium">Premium</option>
              <option value="featured">Vedettes</option>
              <option value="admin">Admins</option>
            </select>
            <button
              onClick={() => { setUserQ(""); setUserFilter({ category: "", country: "", role: "" }); }}
              className="btn-ghost text-xs inline-flex items-center gap-1 px-3"
              data-testid="admin-users-filter-reset"
            >
              <RotateCcw size={12} /> Reset
            </button>
          </div>

          <p className="text-xs text-white/50" data-testid="admin-users-count">
            {users.length} utilisateur{users.length > 1 ? "s" : ""} affiché{users.length > 1 ? "s" : ""}
          </p>

          <div className="flex justify-end -mt-2">
            <button
              onClick={() => setCreatingArtist(true)}
              className="btn-primary inline-flex items-center gap-1.5 text-sm"
              data-testid="admin-create-artist-btn"
            >
              <Plus size={14} /> Créer un profil artiste
            </button>
          </div>

          <div className="border border-white/10 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.04] text-left">
                <tr>
                  <th className="p-3 font-semibold">Utilisateur</th>
                  <th className="p-3 font-semibold">Localisation</th>
                  <th className="p-3 font-semibold">Catégorie</th>
                  <th className="p-3 font-semibold">Badges</th>
                  <th className="p-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`user-row-${u.user_id}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-2.5 min-w-0">
                        {u.picture ? (
                          <img src={u.picture} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-red to-brand-orange flex items-center justify-center font-black text-xs shrink-0">
                            {(u.name || "?").charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="font-semibold truncate">{u.name}</p>
                          <p className="text-[11px] text-white/50 truncate">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-white/70 text-xs">
                      {flagFor(u.country)} {u.city || u.country || "—"}
                    </td>
                    <td className="p-3 text-xs">
                      {u.category ? <span className="tag-pill">{u.category}</span> : <span className="text-white/30">—</span>}
                    </td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-1">
                        {u.is_admin && <span className="tag-pill bg-brand-red/20 text-brand-red border-brand-red/30 text-[10px]">Admin</span>}
                        {u.is_artist && <span className="tag-pill text-[10px]">Artiste</span>}
                        {u.is_verified && <span className="tag-pill bg-brand-yellow/15 text-brand-yellow text-[10px]">✓</span>}
                        {u.is_premium && <span className="tag-pill bg-brand-orange/15 text-brand-orange text-[10px]">Premium</span>}
                        {u.is_featured && <span className="tag-pill bg-brand-red/15 text-brand-red text-[10px]">Vedette</span>}
                      </div>
                    </td>
                    <td className="p-3 text-right">
                      <button
                        onClick={() => setEditingUser(u)}
                        className="inline-flex items-center gap-1 text-xs bg-brand-orange/10 text-brand-orange border border-brand-orange/30 hover:bg-brand-orange/20 rounded-full px-3 py-1.5 transition-colors"
                        data-testid={`admin-user-edit-${u.user_id}`}
                      >
                        <Pencil size={11} /> Éditer
                      </button>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr><td colSpan={5} className="p-8 text-center text-white/50 text-sm">Aucun résultat avec ces filtres.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "import" && <AdminBatchImportPanel />}

      {tab === "seed" && <AdminBulkSeedPanel />}

      {tab === "challenges" && <AdminChallengesPanel />}

      {tab === "ads" && (
        <div className="space-y-4" data-testid="ads-panel">
          {/* FORM création/édition */}
          <form onSubmit={submitAd} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-3" data-testid="ad-form">
            <div className="flex items-center justify-between mb-2">
              <p className="font-display text-lg font-black">{editingAd ? "Modifier la publicité" : "Nouvelle publicité"}</p>
              {editingAd && <button type="button" onClick={resetAdForm} className="text-xs text-white/50 hover:text-white">Annuler l'édition</button>}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <input required value={adForm.title} onChange={(e) => setAdForm({ ...adForm, title: e.target.value })} placeholder="Titre *" className={adInputCls} data-testid="ad-title" />
              <select value={adForm.placement} onChange={(e) => setAdForm({ ...adForm, placement: e.target.value })} className={adInputCls} data-testid="ad-placement">
                <option value="banner">🏠 Bannière Home (top)</option>
                <option value="sidebar">📍 Sidebar (colonne droite)</option>
                <option value="explore">🔍 Page Explorer (inline)</option>
                <option value="artist">🎤 Page Artiste (fiche)</option>
                <option value="track">🎵 Page Morceau (avant lecteur)</option>
                <option value="footer">⬇️ Avant le footer</option>
                <option value="chat">💬 Chat (sous titre)</option>
                <option value="popup">⚡ Popup (intrusif)</option>
              </select>
            </div>

            {/* CIBLAGE CAMPAGNE */}
            <div className="rounded-xl border border-brand-yellow/20 bg-brand-yellow/[0.03] p-3 space-y-3">
              <p className="text-[10px] uppercase tracking-widest text-brand-yellow font-bold">🎯 Ciblage de la campagne</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <select value={adForm.category} onChange={(e) => setAdForm({ ...adForm, category: e.target.value })} className={adInputCls} data-testid="ad-category">
                  <option value="">🌐 Toutes les rubriques</option>
                  <option value="music">🎵 Musique</option>
                  <option value="humor">😂 Humour</option>
                  <option value="cinema">🎬 Cinéma</option>
                  <option value="dance">💃 Danse</option>
                  <option value="art">🎨 Art</option>
                  <option value="fashion">👗 Mode</option>
                  <option value="food">🍲 Cuisine</option>
                  <option value="other">⭐ Autre</option>
                </select>
                <select value={adForm.target_country} onChange={(e) => setAdForm({ ...adForm, target_country: e.target.value })} className={adInputCls} data-testid="ad-country">
                  <option value="">🌍 Tous les pays</option>
                  <option value="Madagascar">🇲🇬 Madagascar</option>
                  <option value="France">🇫🇷 France</option>
                  <option value="Reunion">🇷🇪 Réunion</option>
                  <option value="Mayotte">🇾🇹 Mayotte</option>
                  <option value="Comores">🇰🇲 Comores</option>
                  <option value="Maurice">🇲🇺 Maurice</option>
                </select>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-white/50 uppercase font-bold block mb-1">Date début</label>
                  <input type="datetime-local" value={adForm.start_date} onChange={(e) => setAdForm({ ...adForm, start_date: e.target.value })} className={adInputCls} data-testid="ad-start-date" />
                </div>
                <div>
                  <label className="text-[10px] text-white/50 uppercase font-bold block mb-1">Date fin</label>
                  <input type="datetime-local" value={adForm.end_date} onChange={(e) => setAdForm({ ...adForm, end_date: e.target.value })} className={adInputCls} data-testid="ad-end-date" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-white/50 uppercase font-bold block mb-1">Priorité (0-100, + élevé = + visible)</label>
                  <input type="number" min="0" max="100" value={adForm.priority} onChange={(e) => setAdForm({ ...adForm, priority: e.target.value })} className={adInputCls} data-testid="ad-priority" />
                </div>
                <div>
                  <label className="text-[10px] text-white/50 uppercase font-bold block mb-1">Budget (Ariary, optionnel)</label>
                  <input type="number" min="0" value={adForm.budget_ar} onChange={(e) => setAdForm({ ...adForm, budget_ar: e.target.value })} placeholder="ex: 50000" className={adInputCls} data-testid="ad-budget" />
                </div>
              </div>
            </div>
            <textarea value={adForm.description} onChange={(e) => setAdForm({ ...adForm, description: e.target.value })} placeholder="Description / accroche" rows={2} className={adInputCls} data-testid="ad-desc" />
            <div className="grid sm:grid-cols-2 gap-3">
              <input value={adForm.link_url} onChange={(e) => setAdForm({ ...adForm, link_url: e.target.value })} placeholder="URL cible (ex: https://...)" className={adInputCls} data-testid="ad-link" />
              <input value={adForm.advertiser_name} onChange={(e) => setAdForm({ ...adForm, advertiser_name: e.target.value })} placeholder="Annonceur" className={adInputCls} data-testid="ad-advertiser" />
            </div>
            {/* Image : upload ou URL */}
            <div className="rounded-xl border border-dashed border-white/15 p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-white/60">Image (optionnelle)</p>
                <button type="button" onClick={() => adFileRef.current?.click()} disabled={adUploading} className="btn-ghost text-xs px-3 py-1.5 inline-flex items-center gap-1" data-testid="ad-upload-btn">
                  <ImageIcon size={12} /> {adUploading ? "Upload..." : "Uploader"}
                </button>
                <input ref={adFileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadAdImage(e.target.files[0])} />
              </div>
              <input value={adForm.image_url} onChange={(e) => setAdForm({ ...adForm, image_url: e.target.value })} placeholder="ou colle une URL d'image ici" className={adInputCls} data-testid="ad-image-url" />
              {adForm.image_url && <img src={adForm.image_url} alt="preview" className="mt-2 w-full max-h-32 object-cover rounded-lg" />}
            </div>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={adForm.active} onChange={(e) => setAdForm({ ...adForm, active: e.target.checked })} className="accent-brand-red" data-testid="ad-active" />
                <span>Active immédiatement</span>
              </label>
              <button type="submit" className="btn-primary inline-flex items-center gap-2" data-testid="ad-submit-btn">
                <Plus size={14} /> {editingAd ? "Enregistrer" : "Créer la pub"}
              </button>
            </div>
          </form>

          {/* LISTE */}
          <div className="space-y-2">
            <p className="text-xs text-white/50 uppercase tracking-widest font-bold px-2 py-1">{ads.length} publicité{ads.length > 1 ? "s" : ""}</p>
            {ads.length === 0 && <p className="text-white/50 text-sm py-6 text-center">Aucune pub pour l'instant.</p>}
            {ads.map((ad) => (
              <div key={ad.ad_id} className="flex items-center gap-3 p-4 rounded-xl border border-white/10 bg-white/[0.02]" data-testid={`ad-row-${ad.ad_id}`}>
                {ad.image_url && <img src={ad.image_url} alt="" className="w-16 h-16 rounded-lg object-cover shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold truncate">{ad.title}</p>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${ad.active ? "bg-[#0B8B47]/20 text-[#12A859]" : "bg-white/10 text-white/50"}`}>
                      {ad.active ? "ACTIVE" : "INACTIVE"}
                    </span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-brand-yellow/15 text-brand-yellow font-bold uppercase">{ad.placement}</span>
                    {ad.category && <span className="text-[10px] px-2 py-0.5 rounded-full bg-brand-orange/15 text-brand-orange font-bold uppercase">{ad.category}</span>}
                    {ad.target_country && <span className="text-[10px] px-2 py-0.5 rounded-full bg-brand-red/15 text-brand-red font-bold">{ad.target_country}</span>}
                    {(ad.start_date || ad.end_date) && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-white/60 font-mono">
                        {ad.start_date ? new Date(ad.start_date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) : "?"}
                        {" → "}
                        {ad.end_date ? new Date(ad.end_date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) : "?"}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-white/55 truncate mt-0.5">{ad.description || "—"}</p>
                  <p className="text-[11px] text-white/40 mt-1">
                    👁 {ad.impressions || 0} vues · 🖱 {ad.clicks || 0} clics
                    {ad.impressions > 0 && <> · CTR {((ad.clicks || 0) / ad.impressions * 100).toFixed(1)}%</>}
                    {ad.priority > 0 && <> · ⭐ Priorité {ad.priority}</>}
                    {" · " + (ad.advertiser_name || "—")}
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={!!ad.active} onChange={() => toggleAd(ad)} className="sr-only peer" data-testid={`toggle-ad-${ad.ad_id}`} />
                  <div className="w-11 h-6 bg-white/10 rounded-full peer peer-checked:bg-brand-red transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" />
                </label>
                <button onClick={() => startEditAd(ad)} className="text-white/50 hover:text-brand-orange transition-colors p-1" data-testid={`edit-ad-${ad.ad_id}`} aria-label="Modifier"><Settings size={15} /></button>
                <button onClick={() => deleteAd(ad)} className="text-white/50 hover:text-red-500 transition-colors p-1" data-testid={`delete-ad-${ad.ad_id}`} aria-label="Supprimer"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "surveys" && <AdminSurveysPanel />}
      {tab === "leads" && <AdminLeadsPanel />}
      {tab === "advertisers" && <AdminAdvertisersPanel />}
      {tab === "adsense" && <AdminAdSensePanel />}
      {tab === "dating" && <AdminDatingPanel />}
      {tab === "viral" && canAccessViralStudio && <AdminViralStudioPanel />}
      {tab === "topmada" && (
        <div className="space-y-8" data-testid="topmada-merged-panel">
          <AdminTopMadaVideosPanel />
          <div className="h-px bg-gradient-to-r from-transparent via-amber-400/30 to-transparent" />
          <AdminTopMadaPanel />
        </div>
      )}
      {tab === "madawalls" && canAccessViralStudio && <AdminMadaWallsPanel />}
      {tab === "videoai" && canAccessViralStudio && <AdminVideoStudioPanel />}
      {tab === "studio-ia-local" && <AdminStudioIALocalPanel />}
      {tab === "arnak" && canAccessArnak && <AdminArnakPanel />}

      {tab === "claims" && (
        <div className="space-y-3" data-testid="claims-panel">          {claims.length === 0 && (
            <p className="text-white/60 text-sm">Aucune réclamation reçue pour l'instant.</p>
          )}
          {claims.map((c) => {
            const match = c.link?.match(/\/artist\/([^/]+)/);
            const artistId = match?.[1];
            return (
              <div key={c.notif_id} className={`p-4 rounded-xl border ${c.read ? "border-white/10" : "border-brand-red/40 bg-brand-red/5"}`} data-testid={`claim-row-${c.notif_id}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <p className="font-semibold">{c.title}</p>
                    <p className="text-xs text-white/50">{new Date(c.created_at.replace(" ", "T") + "Z").toLocaleString("fr-FR")}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {artistId && (
                      <a href={`/artist/${artistId}`} target="_blank" rel="noopener noreferrer" className="btn-ghost text-xs inline-flex items-center gap-1" data-testid={`view-claim-${c.notif_id}`}>
                        <ExternalLink size={12} /> Voir profil
                      </a>
                    )}
                    <button onClick={() => transferProfile(c)} className="btn-primary text-xs inline-flex items-center gap-1" data-testid={`transfer-claim-${c.notif_id}`}>
                      <Send size={12} /> Transférer
                    </button>
                  </div>
                </div>
                <pre className="text-xs text-white/80 bg-black/30 rounded-lg p-3 whitespace-pre-wrap font-mono">{c.body}</pre>
              </div>
            );
          })}
        </div>
      )}

      {editingUser && (
        <AdminUserEditModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onUpdated={(u) => {
            setUsers((prev) => prev.map((x) => (x.user_id === u.user_id ? { ...x, ...u } : x)));
            setEditingUser((prev) => (prev ? { ...prev, ...u } : prev));
          }}
        />
      )}

      {creatingArtist && (
        <AdminCreateArtistModal
          onClose={() => setCreatingArtist(false)}
          onCreated={(u) => {
            setUsers((prev) => [u, ...prev]);
            loadUsers();
          }}
        />
      )}
    </div>
  );
};

const StatCard = ({ label, value, accent, small }) => (
  <div className={`rounded-xl border ${accent ? "border-brand-red/30 bg-brand-red/5" : "border-white/10 bg-white/[0.02]"} p-4`}>
    <p className={`font-display font-black ${small ? "text-2xl" : "text-3xl"} ${accent ? "text-brand-red" : "text-white"}`}>{value}</p>
    <p className="text-xs uppercase tracking-widest text-white/50 mt-1">{label}</p>
  </div>
);
const TabBtn = ({ active, onClick, icon, label, testid }) => (
  <button onClick={onClick} className={`px-3 md:px-4 py-2 text-xs md:text-sm font-semibold inline-flex items-center gap-1.5 md:gap-2 border-b-2 transition-colors shrink-0 whitespace-nowrap ${active ? "border-brand-red text-white" : "border-transparent text-white/50 hover:text-white"}`} data-testid={testid}>
    {icon} {label}
  </button>
);

export default AdminPage;
